import type { PolicyCharge } from '@/services/policy-charges';
import { BRAZIL_TIME_ZONE, formatDateToBrazil } from '@/shared/utils';

/**
 * " até dd/mm/aaaa" do mandato que aguarda o segurado autorizar. O prazo vem da
 * API como instante UTC (o fim do dia em Brasília), então o dia é lido em
 * Brasília, não no fuso do navegador. A Autra limita o prazo a 30 dias.
 */
function authorizationDeadline(charge: PolicyCharge): string {
  const deadline = formatDateToBrazil(charge.expireDate, BRAZIL_TIME_ZONE);
  return deadline ? ` até ${deadline}` : '';
}

/**
 * Resumo do PIX Automático na aba Cobrança, pelo estado do mandato. Vai só na 1ª
 * parcela coberta, para não repetir.
 */
export function recurrenceSummary(charge: PolicyCharge): string | undefined {
  const paid = charge.paidInstallments ?? 0;
  const total =
    charge.installmentCount ?? charge.coveredInstallments?.length ?? 0;
  const pendingCancel =
    charge.mandateCancelRequest &&
    (charge.status === 'PENDING' || charge.status === 'PARTIALLY_PAID');

  if (pendingCancel)
    return 'Cancelamento solicitado — aguardando a Finance.API';
  switch (charge.status) {
    case 'PENDING':
      return charge.recurrenceAuthorizedAt
        ? `PIX Automático autorizado em ${formatDateToBrazil(charge.recurrenceAuthorizedAt)} · ${paid} de ${total} pagas`
        : `Aguardando o segurado autorizar no banco${authorizationDeadline(charge)} · ${paid} de ${total} pagas`;
    case 'PARTIALLY_PAID':
      return `PIX Automático · ${paid} de ${total} pagas`;
    case 'PAID':
      return `PIX Automático · ${total} de ${total} pagas`;
    case 'REFUSED':
      return 'PIX Automático recusado pelo segurado';
    case 'EXPIRED':
      return 'O segurado não autorizou o PIX Automático no prazo';
    case 'CANCELLED':
      if (charge.cancelReason === 'MANDATE_START_MISSED') {
        return 'PIX Automático não criado a tempo da 1ª mensalidade — cobre as parcelas por link';
      }
      if (charge.mandateCancelRequest?.reason === 'POLICY_CANCELLED') {
        return 'PIX Automático cancelado — apólice cancelada';
      }
      if (charge.mandateCancelRequest?.reason === 'MANUAL') {
        return `PIX Automático cancelado pelo financeiro${
          charge.mandateCancelRequest.userName
            ? ` (${charge.mandateCancelRequest.userName})`
            : ''
        }`;
      }
      // Sem pedido nosso: quem cancelou vem da Finance.API. Sem essa
      // informação (todas as parcelas canceladas uma a uma), não dá para
      // afirmar que foi o segurado.
      return charge.mandateCanceledBy === 'PAYER'
        ? 'PIX Automático cancelado pelo segurado no banco'
        : 'PIX Automático cancelado fora do Portal';
    default:
      return undefined;
  }
}
